import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { useContextStore } from "@/store/context";
import { rememberStudioHostForApplication } from "@/utils/studioHostPreference";
import {
  isChiefOfStaffDeployment,
  studioNodeImpliesGlobalScope,
} from "@/utils/instanceRole";

export type StudioNewThreadDraft = {
  agentId: string;
  applicationKey?: string;
  environmentName: string;
  title?: string;
  goalTitle?: string;
  goalDescription?: string;
};

const ENVIRONMENTS = ["development", "sandbox", "staging", "production"] as const;

export default function StudioNewThreadDialog({
  open,
  pending,
  requireGoal,
  presetAgentId,
  presetApplicationKey,
  presetEnvironment,
  lockAgent,
  onOpenChange,
  onSubmit,
}: {
  open: boolean;
  pending?: boolean;
  requireGoal?: boolean;
  presetAgentId?: string | null;
  presetApplicationKey?: string | null;
  presetEnvironment?: string | null;
  lockAgent?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (draft: StudioNewThreadDraft) => void;
}) {
  const { t } = useTranslation();
  const deployments = useContextStore((state) => state.deployments);
  const chiefOfStaff = useContextStore((state) => state.chiefOfStaff);
  const applications = useContextStore((state) => state.applications);
  const storeEnv = useContextStore((state) => state.env);
  const [agentId, setAgentId] = useState("");
  const [applicationKey, setApplicationKey] = useState("");
  const [environmentName, setEnvironmentName] = useState("development");
  const [goalTitle, setGoalTitle] = useState("");
  const [goalDescription, setGoalDescription] = useState("");

  useEffect(() => {
    if (!open) return;
    setAgentId(presetAgentId?.trim() || "");
    setApplicationKey(presetApplicationKey?.trim() || "");
    setEnvironmentName(
      (presetEnvironment || storeEnv || "development").trim() || "development",
    );
    setGoalTitle("");
    setGoalDescription("");
  }, [open, presetAgentId, presetApplicationKey, presetEnvironment, storeEnv]);

  const canSubmit = Boolean(agentId.trim()) && !pending && (!requireGoal || goalTitle.trim());
  const hideContextualScope = Boolean(
    lockAgent && studioNodeImpliesGlobalScope(presetAgentId, chiefOfStaff?.id),
  );

  const agentOptions = useMemo(() => {
    const business = deployments
      .filter((item) => item.isAdmin !== true && !isChiefOfStaffDeployment(item))
      .map((item) => ({ id: item.id, label: item.name || item.id }));
    if (
      lockAgent &&
      presetAgentId &&
      !business.some((item) => item.id === presetAgentId)
    ) {
      const lockedLabel =
        chiefOfStaff?.id === presetAgentId
          ? t("sidebar.chiefOfStaff")
          : presetAgentId;
      return [{ id: presetAgentId, label: lockedLabel }, ...business];
    }
    return business;
  }, [chiefOfStaff, deployments, lockAgent, presetAgentId, t]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t(
              requireGoal
                ? "agentSpec.studioFromGoalTitle"
                : "agentSpec.studioNewThreadTitle",
            )}
          </DialogTitle>
          <DialogDescription>
            {t(
              hideContextualScope
                ? "agentSpec.studioNewThreadContextualDescription"
                : "agentSpec.studioNewThreadDescription",
            )}
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!canSubmit) return;
            const host = agentId.trim();
            const app = hideContextualScope ? "" : applicationKey.trim();
            if (app) rememberStudioHostForApplication(app, host);
            onSubmit({
              agentId: host,
              applicationKey: app || undefined,
              environmentName: environmentName.trim() || "development",
              goalTitle: goalTitle.trim() || undefined,
              goalDescription: goalDescription.trim() || undefined,
            });
          }}
        >
          {hideContextualScope ? null : (
            <>
              <div className="space-y-2">
                <label htmlFor="studio-new-thread-agent" className="text-sm font-medium">
                  {t("agentSpec.studioHostAgentLabel")}
                </label>
                <select
                  id="studio-new-thread-agent"
                  data-testid="studio-new-thread-agent"
                  value={agentId}
                  disabled={pending || lockAgent}
                  onChange={(event) => setAgentId(event.target.value)}
                  required
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">{t("agentSpec.studioHostAgentPlaceholder")}</option>
                  {agentOptions.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label htmlFor="studio-new-thread-app" className="text-sm font-medium">
                  {t("agentSpec.studioScopeApplicationLabel")}
                </label>
                <select
                  id="studio-new-thread-app"
                  data-testid="studio-new-thread-app"
                  value={applicationKey}
                  disabled={pending}
                  onChange={(event) => setApplicationKey(event.target.value)}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">{t("agentSpec.studioScopeApplicationNone")}</option>
                  {applications.map((app) => (
                    <option key={app.key} value={app.key}>
                      {app.display_name || app.key}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label htmlFor="studio-new-thread-env" className="text-sm font-medium">
                  {t("agentSpec.studioEnvironmentLabel")}
                </label>
                <select
                  id="studio-new-thread-env"
                  data-testid="studio-new-thread-env"
                  value={environmentName}
                  disabled={pending}
                  onChange={(event) => setEnvironmentName(event.target.value)}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  {ENVIRONMENTS.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
          {requireGoal ? (
            <>
              <div className="space-y-2">
                <label htmlFor="studio-from-goal-title" className="text-sm font-medium">
                  {t("agentSpec.studioFromGoalTitleLabel")}
                </label>
                <Input
                  id="studio-from-goal-title"
                  data-testid="studio-new-thread-goal-title"
                  value={goalTitle}
                  onChange={(event) => setGoalTitle(event.target.value)}
                  placeholder={t("agentSpec.studioFromGoalTitlePlaceholder")}
                  disabled={pending}
                  required
                />
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="studio-from-goal-description"
                  className="text-sm font-medium"
                >
                  {t("agentSpec.studioFromGoalDescriptionLabel")}
                </label>
                <textarea
                  id="studio-from-goal-description"
                  value={goalDescription}
                  onChange={(event) => setGoalDescription(event.target.value)}
                  placeholder={t("agentSpec.studioFromGoalDescriptionPlaceholder")}
                  disabled={pending}
                  rows={3}
                  className="min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
            </>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={pending}
            >
              {t("agentSpec.studioFromGoalCancel")}
            </Button>
            <Button type="submit" disabled={!canSubmit} data-testid="studio-new-thread-submit">
              {pending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
              ) : null}
              {t(
                pending
                  ? "agentSpec.studioFromGoalPending"
                  : requireGoal
                    ? "agentSpec.studioFromGoalConfirm"
                    : "agentSpec.studioCreateThread",
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
